import { useCallback, useEffect, useRef, useState } from 'react';
import { useXpSummary } from './useXpSummary';

export interface XpGain {
  /** Unique per emission so the flash host can remount and replay the animation. */
  id: number;
  /** XP just earned (the positive delta in total XP). */
  amount: number;
}

let nextGainId = 1;

/**
 * Emits a positive XP delta whenever the fetched summary's total climbs *without* a
 * level-up — a level-up gets the bigger LevelUpCelebration / rank ceremony instead, so we
 * don't double up. The first summary for a user (or after a user switch) only records
 * the baseline. Pairs with useXpLiveSync, which makes the summary refetch promptly.
 */
export function useXpGain(): { gain: XpGain | null; clear: () => void } {
  const { data, userId } = useXpSummary();
  const prevRef = useRef<{ userId: string; totalXp: number; level: number } | null>(null);
  const [gain, setGain] = useState<XpGain | null>(null);

  useEffect(() => {
    if (!data || !userId) return;
    const prev = prevRef.current;
    prevRef.current = { userId, totalXp: data.totalXp, level: data.level };
    // Baseline on first load or when the signed-in user changes — celebrate nothing.
    if (!prev || prev.userId !== userId) return;
    const delta = data.totalXp - prev.totalXp;
    if (delta > 0 && data.level === prev.level) {
      setGain({ id: nextGainId++, amount: delta });
    }
  }, [data, userId]);

  const clear = useCallback(() => setGain(null), []);
  return { gain, clear };
}
